import { useCallback, useEffect, useRef, useState } from "react";

/** The time now, asked again every minute so "5 min ago" stays true. */
export function useNow(intervalMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

interface ArrivalState {
  ids: readonly string[];
  known: ReadonlySet<string>;
  arrivals: ReadonlySet<string>;
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

/**
 * The ids that are new since the list last changed: they pop in. Nothing is new the first time, so a page that
 * opens does not animate everything at once.
 */
export function useArrivals(ids: readonly string[]): ReadonlySet<string> {
  const [state, setState] = useState<ArrivalState>(() => ({ ids, known: new Set(ids), arrivals: new Set<string>() }));
  if (state.ids === ids || sameIds(state.ids, ids)) return state.arrivals;
  const arrivals = new Set<string>();
  if (state.known.size > 0) for (const id of ids) if (!state.known.has(id)) arrivals.add(id);
  setState({ ids, known: new Set(ids), arrivals });
  return arrivals;
}

/**
 * Whether an element has come near the screen. Without IntersectionObserver (an old browser, a test) it counts as
 * seen at once. Once seen it stays seen, so a tile that has read its map does not read it again.
 */
export function useSeen<T extends Element>(margin = "200px"): [(node: T | null) => void, boolean] {
  const supported = typeof IntersectionObserver !== "undefined";
  const [seen, setSeen] = useState(!supported);
  const observer = useRef<IntersectionObserver | null>(null);
  const ref = useCallback(
    (node: T | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!node || !supported) return;
      const next = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setSeen(true);
            next.disconnect();
          }
        },
        { rootMargin: margin },
      );
      next.observe(node);
      observer.current = next;
    },
    [margin, supported],
  );
  useEffect(() => () => observer.current?.disconnect(), []);
  return [ref, seen];
}

/** True while motion is on (the profile switch and the system setting both say so). */
export function motionIsOn(): boolean {
  return typeof document === "undefined" || document.documentElement.getAttribute("data-motion") !== "off";
}
